import React, { useState, useEffect, useCallback } from "react";
import { Plus, PlayCircle, RotateCw, X, Eye, Server, CircleDot, CheckCircle2, XCircle, Loader2, HeartPulse, Rocket } from "lucide-react";
import { Link } from "react-router-dom";
import api, { notifyError } from "@/lib/api";
import PlatformLayout from "@/components/PlatformLayout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ExecutionStatusBadge, AdapterBadge, HealthBadge } from "./infrastructure/InfrastructureBadges";
import InfrastructureFormDialog from "./infrastructure/InfrastructureFormDialog";

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}
function dur(sec) {
  if (sec == null) return "—";
  const s = Math.round(sec);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
}

function SummaryCard({ label, value, tone = "neutral", icon: Icon, testid }) {
  const tones = {
    neutral: "text-white/70",
    running: "text-amber-300",
    success: "text-emerald-300",
    failed: "text-rose-300",
    pending: "text-sky-300",
    cancelled: "text-neutral-300",
  };
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1.5">
        {Icon ? <Icon size={14} className={tones[tone]} /> : null}
        <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold">{label}</div>
      </div>
      <div className={`text-2xl font-black font-display ${tones[tone]}`}>{value}</div>
    </div>
  );
}

const RUNNING_STATUSES = new Set(["CONNECTING", "UPLOADING", "EXECUTING", "HEALTHCHECK", "ROLLING_BACK"]);

export default function PlatformInfrastructure() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ex, ts, rs] = await Promise.all([
        api.get("/platform/infrastructure"),
        api.get("/platform/tenants"),
        api.get("/platform/deployment-runner", { params: { status: "SUCCESS", limit: 50 } }),
      ]);
      setItems(ex.data.items || []);
      setSummary(ex.data.summary || null);
      setTenants(ts.data.items || []);
      setRuns(rs.data.items || []);
    } catch (e) { notifyError(e); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const action = async (id, verb, body = {}) => {
    setBusyId(id + verb);
    try {
      await api.post(`/platform/infrastructure/${id}/${verb}`, body);
      toast.success(`Execution ${verb} berhasil.`);
      load();
    } catch (e) { notifyError(e, `Gagal ${verb}`); }
    finally { setBusyId(null); }
  };

  const cancel = (id) => {
    const reason = window.prompt("Alasan cancel?") || "";
    action(id, "cancel", { reason });
  };

  const tenantOf = (id) => tenants.find((t) => t.tenant_id === id);
  const runningCount = ["connecting", "uploading", "validating", "executing", "healthcheck", "rolling_back"]
    .reduce((acc, k) => acc + (summary?.[k] || 0), 0);

  return (
    <PlatformLayout title="Infrastructure">
      <div data-testid="platform-infrastructure-page">
        <div className="grid grid-cols-2 md:grid-cols-7 gap-3 mb-5">
          <SummaryCard label="Total" value={summary?.total ?? 0} icon={Server} testid="infra-summary-total" />
          <SummaryCard label="Pending" value={summary?.pending ?? 0} tone="pending" icon={CircleDot} testid="infra-summary-pending" />
          <SummaryCard label="Running" value={runningCount} tone="running" icon={Loader2} testid="infra-summary-running" />
          <SummaryCard label="Success" value={summary?.success ?? 0} tone="success" icon={CheckCircle2} testid="infra-summary-success" />
          <SummaryCard label="Deployed" value={summary?.deployed ?? 0} tone="success" icon={Rocket} testid="infra-summary-deployed" />
          <SummaryCard label="Failed" value={summary?.failed ?? 0} tone="failed" icon={XCircle} testid="infra-summary-failed" />
          <SummaryCard label="Cancelled" value={summary?.cancelled ?? 0} tone="cancelled" icon={HeartPulse} testid="infra-summary-cancelled" />
        </div>

        <div className="flex items-center gap-3 mb-4">
          <div className="text-xs text-white/50">Menampilkan {items.length} execution</div>
          <Button onClick={() => setDialogOpen(true)} className="ml-auto bg-white text-black hover:bg-white/90" data-testid="btn-create-execution">
            <Plus size={14} className="mr-1" /> Buat Execution
          </Button>
        </div>

        {loading ? (
          <div className="text-white/40 text-sm p-6 rounded-2xl border border-white/10 text-center">Memuat…</div>
        ) : items.length === 0 ? (
          <div className="text-white/40 text-sm p-8 rounded-2xl border border-white/10 text-center">
            Belum ada execution. Klik <strong className="text-white">+ Buat Execution</strong>.
          </div>
        ) : (
          <div className="rounded-2xl border border-white/10 overflow-hidden">
            <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 bg-white/5 text-[10px] uppercase tracking-wider text-white/40 font-bold">
              <div className="col-span-3">Execution #</div>
              <div className="col-span-2">Tenant</div>
              <div className="col-span-1">Adapter</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-1">Duration</div>
              <div className="col-span-1">Started</div>
              <div className="col-span-2 text-right">Action</div>
            </div>
            {items.map((e) => {
              const t = tenantOf(e.tenant_id);
              const canCancel = e.status === "PENDING" || RUNNING_STATUSES.has(e.status);
              const canExec = e.status === "PENDING";
              const canRetry = e.status === "FAILED";
              return (
                <div key={e.id} className="grid grid-cols-1 md:grid-cols-12 gap-2 px-4 py-3 border-t border-white/5 items-center"
                  data-testid={`infra-row-${e.id}`}>
                  <div className="col-span-3 min-w-0">
                    <Link to={`/platform/infrastructure/${e.id}`} className="text-sm font-mono font-bold text-white hover:text-emerald-300 truncate block" data-testid={`infra-number-${e.id}`}>
                      {e.execution_number}
                    </Link>
                    <div className="text-[10px] text-white/40 truncate font-mono">{e.run_number || "—"}</div>
                  </div>
                  <div className="col-span-2 min-w-0">
                    <div className="text-xs font-bold text-white truncate">{t?.tenant_name || e.tenant_id.slice(0, 8)}</div>
                    <div className="text-[10px] text-white/40 font-mono">{t?.tenant_code || "—"}</div>
                  </div>
                  <div className="col-span-1"><AdapterBadge adapter={e.adapter} /></div>
                  <div className="col-span-2 flex items-center gap-2 flex-wrap">
                    <ExecutionStatusBadge status={e.status} size="sm" />
                    <HealthBadge result={e.health_result} />
                  </div>
                  <div className="col-span-1 text-xs text-white/60 font-mono">{dur(e.duration_seconds)}</div>
                  <div className="col-span-1 text-[11px] text-white/40">{fmt(e.started_at || e.created_at)}</div>
                  <div className="col-span-2 flex gap-1 justify-end">
                    <Link to={`/platform/infrastructure/${e.id}`} className="w-7 h-7 rounded-lg grid place-items-center text-white/60 hover:text-white hover:bg-white/10"
                      title="View" data-testid={`infra-view-${e.id}`}>
                      <Eye size={12} />
                    </Link>
                    {canExec && (
                      <button onClick={() => action(e.id, "execute", { simulate: "success" })} disabled={busyId === e.id + "execute"}
                        className="w-7 h-7 rounded-lg grid place-items-center text-emerald-400 hover:bg-emerald-500/10 disabled:opacity-50"
                        title="Execute" data-testid={`infra-execute-${e.id}`}>
                        <PlayCircle size={12} />
                      </button>
                    )}
                    {canRetry && (
                      <button onClick={() => action(e.id, "retry")} disabled={busyId === e.id + "retry"}
                        className="w-7 h-7 rounded-lg grid place-items-center text-amber-400 hover:bg-amber-500/10 disabled:opacity-50"
                        title="Retry" data-testid={`infra-retry-${e.id}`}>
                        <RotateCw size={12} />
                      </button>
                    )}
                    {canCancel && (
                      <button onClick={() => cancel(e.id)} disabled={busyId === e.id + "cancel"}
                        className="w-7 h-7 rounded-lg grid place-items-center text-rose-400 hover:bg-rose-500/10 disabled:opacity-50"
                        title="Cancel" data-testid={`infra-cancel-${e.id}`}>
                        <X size={12} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <InfrastructureFormDialog runs={runs} open={dialogOpen} onClose={() => setDialogOpen(false)} onCreated={load} />
      </div>
    </PlatformLayout>
  );
}
